import React from 'react';

import type {
	ModalBodyProps,
	ModalDialogProps,
	ModalFooterProps,
	ModalHeaderProps,
	ModalTitleProps,
} from '@atlaskit/modal-dialog';
import type { ButtonProps } from '@atlaskit/button/new';

import type { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';
import { useWhiteboardInternalModal } from './useWhiteboardInternalModal';
import { WhiteboardInternalDialog } from './WhiteboardInternalDialog';
import { WhiteboardInternalOnboardingSlidesModal } from './WhiteboardInternalOnboardingSlidesModal';

export type WhiteboardInternalModalProps = {
	modalHeader: {
		title: {
			content: string;
			props?: Omit<ModalTitleProps, 'children'>;
		};
		props?: Omit<ModalHeaderProps, 'children'>;
	};
	modalBody: {
		content: string;
		props?: Omit<ModalBodyProps, 'children'>;
	};
	modalFooter: {
		buttons: {
			label: string;
			shouldCloseModal?: boolean;
			props?: Omit<ButtonProps, 'children'>;
		}[];
		props?: Omit<ModalFooterProps, 'children'>;
	};
	props?: Omit<ModalDialogProps, 'children'>;
	image?: string;
};

export interface WhiteboardInternalModalConfiguration {
	type: 'dialog' | 'dialogList';
}

export interface WhiteboardDialogConfiguration
	extends WhiteboardInternalModalConfiguration,
		WhiteboardInternalModalProps {
	type: 'dialog';
}

export interface WhiteboardOnboardingDialogListConfiguration
	extends WhiteboardInternalModalConfiguration {
	type: 'dialogList';
	dialogList: WhiteboardInternalModalProps[];
	props?: Omit<ModalDialogProps, 'children'>;
	bodyTextHeight?: string;
}

export type WhiteboardModalProps = {
	whiteboardInternalModalService: WhiteboardInternalModalService;
};

export const WhiteboardInternalModal: React.FC<WhiteboardModalProps> = ({
	whiteboardInternalModalService,
}: WhiteboardModalProps) => {
	const { modalState, closeModal } = useWhiteboardInternalModal(whiteboardInternalModalService);

	if (modalState.isModalOpen) {
		const modalType = modalState.config.type;
		if (modalType === 'dialogList') {
			return (
				<WhiteboardInternalOnboardingSlidesModal
					{...(modalState.config as WhiteboardOnboardingDialogListConfiguration)}
					closeModal={closeModal}
				/>
			);
		} else if (modalType === 'dialog') {
			return (
				<WhiteboardInternalDialog
					modalProps={modalState.config as WhiteboardDialogConfiguration}
					closeModal={closeModal}
				/>
			);
		}
	}

	return null;
};
